// BUILD=R22-multiprint-sync-popups
import { useMemo, useState } from "react";
import { Loader2, Printer, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";
import {
  useOrdersDecoprof,
  useUpdateOrdersDecoprofStatus,
  type OrdersDecoprofRow,
} from "@/hooks/useOrdersDecoprof";
import {
  ORDER_DECOPROF_TYPE_OPTIONS,
  isMobileDevice,
  openPackingSlip,
  type OrderDecoprofType,
} from "@/lib/ordersDecoprof";
import OrdersDecoprofMultiSelect from "./OrdersDecoprofMultiSelect";
import BadgeStatus3D from "./BadgeStatus3D";
import Card3D from "./Card3D";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const OrdersDecoprofTab = () => {
  const { data: orders, isLoading, error } = useOrdersDecoprof();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<OrderDecoprofType[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);

  const allSuppliers = useMemo(() => {
    const set = new Set<string>();
    (orders ?? []).forEach((o) => o.primarySupplier && set.add(o.primarySupplier));
    return Array.from(set).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders ?? [];
    if (typeFilter.length > 0) {
      list = list.filter((o) => typeFilter.includes(o.type));
    }
    if (supplierFilter.length > 0) {
      list = list.filter(
        (o) => o.primarySupplier && supplierFilter.includes(o.primarySupplier),
      );
    }
    return list;
  }, [orders, typeFilter, supplierFilter]);

  const toggleRow = (ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  };

  const selectedRows: OrdersDecoprofRow[] = useMemo(
    () =>
      (orders ?? []).filter(
        (o) => selected.has(o.ordernumber) && o.orderid != null,
      ),
    [orders, selected],
  );

  const canPrintPackingSlip = hasPermission("ordersDecoprof", "read");
  const hasSelection = selected.size >= 1;
  const printDisabled = !canPrintPackingSlip || !hasSelection;
  const printDisabledReason = !canPrintPackingSlip
    ? "Geen toegang tot Orders Decoprof"
    : !hasSelection
      ? "Selecteer minimaal 1 order"
      : "";

  const [isPrinting, setIsPrinting] = useState(false);
  const [isMarkingCreated, setIsMarkingCreated] = useState(false);

  const handlePrint = async () => {
    if (selectedRows.length === 0) return;
    setIsPrinting(true);

    const failed: string[] = [];
    let succeeded = 0;
    const mobile = isMobileDevice();

    console.log(
      `[multi-print] start — ${selectedRows.length} orders, mobile=${mobile}`,
      selectedRows.map((o) => o.ordernumber),
    );

    // STAP 1 (SYNC, in dezelfde user-gesture): open ALLE popups in één tick.
    // Browsers staan alleen meerdere window.open() toe als ze synchroon
    // vanuit dezelfde click-handler gebeuren — vóór elke await.
    const preOpened: Array<{ order: OrdersDecoprofRow; win: Window | null }> = [];
    if (!mobile) {
      for (const order of selectedRows) {
        const win = window.open("about:blank", `_pakbon_${order.ordernumber}`);
        preOpened.push({ order, win });
        console.log(
          `[multi-print] sync open ${order.ordernumber} → ${win ? "OK" : "BLOCKED"}`,
        );
      }
    } else {
      for (const order of selectedRows) preOpened.push({ order, win: null });
    }

    try {
      // STAP 2 (ASYNC): per order fetch + assign blob aan z'n eigen popup.
      for (let i = 0; i < preOpened.length; i++) {
        const { order, win } = preOpened[i];
        console.log(
          `[multi-print] ${i + 1}/${preOpened.length} fetch ${order.ordernumber} (orderid=${order.orderid})`,
        );

        // Op desktop: als deze specifieke popup geblokkeerd is, val terug op
        // <a download> zodat we tóch een PDF afleveren.
        try {
          await openPackingSlip(order.orderid as number, win);
          console.log(`[multi-print] ${order.ordernumber} ✓ delivered`);
        } catch (e: any) {
          console.warn(
            `[multi-print] ${order.ordernumber} ✗ ${e?.message ?? e}`,
          );
          if (win && !win.closed) {
            try { win.close(); } catch { /* ignore */ }
          }
          failed.push(`${order.ordernumber} (${e?.message ?? e})`);
          continue;
        }

        // Status bijwerken — fail mag rest niet blokkeren
        try {
          await updateStatus.mutateAsync({
            ordernumber: order.ordernumber,
            status: "Uitgeprint",
          });
          succeeded++;
          console.log(`[multi-print] ${order.ordernumber} status → Uitgeprint`);
        } catch (e: any) {
          failed.push(`${order.ordernumber} (status: ${e?.message ?? e})`);
          console.warn(
            `[multi-print] ${order.ordernumber} status update faalde: ${e?.message ?? e}`,
          );
        }
      }

      if (succeeded > 0 && failed.length === 0) {
        toast.success(
          `${succeeded} pakbon${succeeded === 1 ? "" : "nen"} afgedrukt en gemarkeerd als Uitgeprint`,
        );
      } else if (succeeded > 0 && failed.length > 0) {
        toast.warning(
          `${succeeded} ok, ${failed.length} mislukt: ${failed.join(", ")}`,
        );
      } else {
        toast.error(`Pakbon afdrukken mislukt: ${failed.join(", ")}`);
      }
    } finally {
      setIsPrinting(false);
    }
  };

  const handleMarkCreated = async () => {
    if (selectedRows.length === 0) return;
    setIsMarkingCreated(true);
    const failed: string[] = [];
    let succeeded = 0;
    try {
      for (const order of selectedRows) {
        try {
          await updateStatus.mutateAsync({
            ordernumber: order.ordernumber,
            status: "Aangemaakt",
          });
          succeeded++;
        } catch (e: any) {
          failed.push(`${order.ordernumber} (${e?.message ?? e})`);
        }
      }
      if (failed.length === 0) {
        toast.success(
          `${succeeded} order${succeeded === 1 ? "" : "s"} gemarkeerd als Aangemaakt`,
        );
      } else {
        toast.warning(
          `${succeeded} ok, ${failed.length} mislukt: ${failed.join(", ")}`,
        );
      }
    } finally {
      setIsMarkingCreated(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <OrdersDecoprofMultiSelect
          label="Type order"
          options={ORDER_DECOPROF_TYPE_OPTIONS as readonly string[]}
          values={typeFilter}
          onChange={(v) => setTypeFilter(v as OrderDecoprofType[])}
        />
        <OrdersDecoprofMultiSelect
          label="Leverancier"
          options={allSuppliers}
          values={supplierFilter}
          onChange={setSupplierFilter}
        />

        <div className="ml-auto flex gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={handleMarkCreated}
                    disabled={
                      printDisabled || updateStatus.isPending || isMarkingCreated || isPrinting
                    }
                    className="gap-2"
                  >
                    {isMarkingCreated ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCheck2 className="h-4 w-4" />
                    )}
                    Aangemaakt
                  </Button>
                </span>
              </TooltipTrigger>
              {printDisabledReason && (
                <TooltipContent>{printDisabledReason}</TooltipContent>
              )}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handlePrint}
                    disabled={
                      printDisabled || updateStatus.isPending || isPrinting || isMarkingCreated
                    }
                    className="gap-2"
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    Pakbon afdrukken
                  </Button>
                </span>
              </TooltipTrigger>
              {printDisabledReason && (
                <TooltipContent>{printDisabledReason}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Resultaten-aantal */}
      {!isLoading && (
        <p className="text-sm text-muted-foreground">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} gevonden
          {selected.size > 0 && ` — ${selected.size} geselecteerd`}
        </p>
      )}

      {/* Tabel */}
      <Card3D className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Ordernummer</TableHead>
              <TableHead>Type order</TableHead>
              <TableHead>Leverancier</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                  Laden...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-destructive">
                  Fout bij laden van orders
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Geen Decoprof-orders gevonden
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow key={order.ordernumber}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(order.ordernumber)}
                      onCheckedChange={() => toggleRow(order.ordernumber)}
                      aria-label={`Selecteer order ${order.ordernumber}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{order.ordernumber}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{order.type}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {order.primarySupplier ?? "Onbekend"}
                  </TableCell>
                  <TableCell>
                    <BadgeStatus3D status={order.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card3D>
    </div>
  );
};

export default OrdersDecoprofTab;
